import TPEAction from '../../models/common/TPEAction';
import { MultidimensionalAllocationState } from './MultidimensionalAllocationState';
import {
    GetMultiAllocationWorksheetsResponse
} from "src/models/multidimensional-allocation/GetMultiAllocationWorksheetsResponse";
import {MultiAllocationWorksheetData} from "src/models/multidimensional-allocation/MultiAllocationWorksheetData";
import CONSTANTS from "src/utils/constants";

/**
 * List here the actions supported by this reducer
 */
export const ACTIONS = Object.freeze({
    SET_LOADING: new TPEAction('SET_LOADING'),
    SET_ERROR: new TPEAction('SET_ERROR'),
    SET_ALLOCATION_CALCULATIONS: new TPEAction('SET_ALLOCATION_CALCULATIONS'),
    RESET_STATE: new TPEAction('RESET_STATE'),
    SET_SELECTED_WORKSHEET: new TPEAction('SET_SELECTED_WORKSHEET'),
    SET_SEVEN_SEGMENTS_LOV_MAP: new TPEAction('SET_SEVEN_SEGMENTS_LOV_MAP'),
    CHECK_AND_REFRESH_ENTITY_DETAILS: new TPEAction('CHECK_AND_REFRESH_ENTITY_DETAILS'),
    SET_DATASET_RECORDS: new TPEAction('SET_DATASET_RECORDS'),
    SET_VIEW_MODE: new TPEAction('SET_VIEW_MODE'),
    SET_DATA_SOURCES_NEED_REFRESH: new TPEAction('SET_DATA_SOURCES_NEED_REFRESH'),
    SET_EXECUTION_PERIOD: new TPEAction('SET_EXECUTION_PERIOD'),
    SET_WORKSHEET_EXECUTION: new TPEAction('SET_WORKSHEET_EXECUTION'),
    SET_EXECUTION_PERIOD_TOUCHED: new TPEAction('SET_EXECUTION_PERIOD_TOUCHED'),
});

/**  
* This function is responsible for updating the state based on action type
* @param state The current multidimensional allocation state
* @param action The current dispatched action 
*/
export function multidimensionalAllocationReducer(state: MultidimensionalAllocationState, action: TPEAction): MultidimensionalAllocationState {
    switch (action.type) {
        case ACTIONS.SET_LOADING.type:
            return {
                ...state,
                isLoading: action.payload,
                error: action.payload ? undefined : state.error, // Clear error when starting to load
            };
        case ACTIONS.SET_ERROR.type:
            return {
                ...state,
                error: action.payload,
                isLoading: false,
            };
        case ACTIONS.SET_ALLOCATION_CALCULATIONS.type:
            return {
                ...state,
                allocationCalculations: action.payload as GetMultiAllocationWorksheetsResponse,
                isLoading: false,
                error: undefined,
            };
        case ACTIONS.RESET_STATE.type:
            return {
                ...state,
                isLoading: false,
            };
        case ACTIONS.SET_SELECTED_WORKSHEET.type:

            return {
                ...state,
                selectedWorksheet: getSelectedWorksheet(action.payload as GetMultiAllocationWorksheetsResponse),
                isLoading: false,
                error: undefined,
            }
        case ACTIONS.SET_SEVEN_SEGMENTS_LOV_MAP.type: {
            return {
                ...state,
                sevenSegmentLOVMap: new Map(action.payload),
            }
        }
        case ACTIONS.CHECK_AND_REFRESH_ENTITY_DETAILS.type: {
            const entityStatus = state.selectedWorksheet?.status;
            if (entityStatus == CONSTANTS.CALCULATION_STATUS.ACTIVE) {
                return {
                    ...state,
                    entityDetailsNeedRefresh: true,
                }
            } else {
                return state;
            }
        }

        case ACTIONS.SET_DATASET_RECORDS.type: {
            return {
                ...state,
                error: undefined,
                dataSourceRecords: action.payload,
                dataSourcesNeedRefresh: false,
            };
        }

        case ACTIONS.SET_VIEW_MODE.type: {
            return {
                ...state,
                viewMode: action.payload,
            }
        }

        case ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.type: {
            return {
                ...state,
                dataSourcesNeedRefresh: action.payload,
            }
        }

        case ACTIONS.SET_EXECUTION_PERIOD.type: {
            return {
                ...state,
                selectedExecutionPeriod: action.payload,
            }
        }

        case ACTIONS.SET_WORKSHEET_EXECUTION.type: {
            return {
                ...state,
                worksheetExecution: action.payload,
            }
        }

        case ACTIONS.SET_EXECUTION_PERIOD_TOUCHED.type: {
            return {
                ...state,
                executionPeriodWasTouched: action.payload,
            }
        }

        default:
            console.warn(`No action found for ${action.type}. Returning unchanged state`);
            return state;
    }
}

function getSelectedWorksheet(worksheetResponse: GetMultiAllocationWorksheetsResponse): MultiAllocationWorksheetData {
    let selected = worksheetResponse.worksheets
        .find(worksheet => worksheet.worksheetVersion === -1);

    if (!selected) {
        // If version -1 doesn't exist, look for version 0
        selected = worksheetResponse.worksheets
            .find(worksheet => worksheet.worksheetVersion === 0);
    }

    // If neither version -1 nor 0 exists, use the first available version
    if (!selected) {
        selected = worksheetResponse.worksheets[0];
    }
    return selected;
}
